import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toggleEditMode, updateWidgetProperty } from 'actions/editor';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from 'scenejs';

import { EditorContext } from '../../index';
import { Container } from '../core/container';
import { Property } from '../../components/properties/';
import { Background } from 'components/Editor/components/properties/background';
import { useChildrenWidgetSelected, useContainerSelector, useHotspotWidgetSelector, useSelectedWidgetSelector, useWidgetSelector } from 'components/Editor/hooks/useEditorSession';
import { BackgroundView } from '../background';
import Portal from 'components/Portal';
import { Widget } from './../../widgets/core';

import { BrandThemeManager } from '@paperflite-marketing/cleverstory-commons';

import styles from './modal.module.scss'
import { PAGE, SECTION } from 'constants/widgetTypes';

const ModalWidget = React.memo(function ModalWidget({ className, style, widgetProps, widgetRef, children }) {
  
  const ref = widgetRef;
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  
  const properties = _.get(widgetProps, 'properties', {});

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const childrenSelection = useChildrenWidgetSelected(editor, id);
  const selectedWidget = useSelectedWidgetSelector(editor)?.[0]

  const hotspotWidget = useHotspotWidgetSelector(editor, breakpoint);
  const hotspotWidgetParent = useWidgetSelector(editor, hotspotWidget && hotspotWidget?.parent);
  const hotspotVisibility = _.get(hotspotWidgetParent, `properties.${breakpoint}.visible`)

  const visibleWitdh = parseInt(_.get(widgetProps, `properties.${breakpoint}.layout.width`)) || 0;
  const widgetHeight = parseInt(_.get(widgetProps, `properties.${breakpoint}.layout.height`)) || 0;

  const offset = breakpoint === 'desktop' ? 100 : 100;

  const tools = useSelector((state) => _.get(state, `${editor}.present.tools`));

  const editView = useSelector(state => _.get(state, `${editor}.present.editView`));

  const background = _.get(properties, 'desktop.background');
  const backdrop = _.get(properties, 'desktop.backdrop');

  const storyboard = useSelector(state => _.get(state, 'storyboards.storyboard.item', null));
  const storyboardType = _.get(storyboard, 'type');
  
  const backdropContainer = document.getElementById(editor);

  var widgetStyle = {
    ...style,
    ...getModalStyles(properties),
    transform: 'none'
  }

  var backgroundStyle = getBackgroundStyle(properties);

  const isSelectedWidgetValid = 
  (useContainerSelector(editor, _.get(selectedWidget, "uid"))?.type === PAGE) && 
  (_.get(selectedWidget, "type") !== SECTION);

  return (
    <>
      {backdropContainer && editView === 'backdrop' &&
        <Portal mountNode={backdropContainer}>
          <BackgroundView background={backdrop} />
        </Portal>
      }     

      <Container widgetProps={widgetProps} widgetRef={widgetRef}>
        <div
          id={id}
          ref={ref}
          className={cx(className, styles.modalWidget, 'cs_modal_widget', {
            [styles.mobile]: breakpoint === 'mobile',
            [styles.partialSelection]: childrenSelection.selected
          })}
          style={widgetStyle}
          data-storyboard-type={storyboardType}
          data-widget-id={id}
          data-widget-type={type}
          data-container={true}
          data-selectable={true}
        >
          <BrandThemeManager className={".cs_text_widget"} themes={_.get(storyboard, 'themes')} />
          <BackgroundView background={background} style={backgroundStyle} />
          {children}

          {( hotspotWidget && hotspotVisibility ) &&
            <Widget key={hotspotWidget.uid} widgetProps={hotspotWidget} />
          }
        </div>
      </Container>
      
      {tools.gridlines &&
        <>
          <div className={cx(styles.boundary, styles.top, {[styles.hideBoundary]: childrenSelection.selected && !isSelectedWidgetValid})} style={{
            top: offset
          }} />
          <div className={cx(styles.boundary, styles.bottom, {[styles.hideBoundary]: childrenSelection.selected && !isSelectedWidgetValid})} style={{
            top: `${widgetHeight + offset}px`
          }} />

          <div className={cx(styles.boundary, styles.left, {[styles.hideBoundary]: childrenSelection.selected && !isSelectedWidgetValid})} style={{
            left: `calc((100% - ${visibleWitdh}px)/2)`,
            height: `${widgetHeight + 300}px`
          }} />
          <div className={cx(styles.boundary, styles.right, {[styles.hideBoundary]: childrenSelection.selected && !isSelectedWidgetValid})} style={{
            right: `calc((100% - ${visibleWitdh}px)/2)`,
            height: `${widgetHeight + 300}px`
          }} />
        </>
      }
    </>
  )
});

const ModalWidgetDesktopAction = (props) => {

  const dispatch = useDispatch();

  const [view, setView] = useState(null)

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, 'uid', null);
  const background = _.get(props, 'properties.desktop.background');
  const backdrop = _.get(props, 'properties.desktop.backdrop');

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false, view));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      <Property.Action className={styles.textButton} onClick={changeView.bind(this, 'background')}>
        Change Background
      </Property.Action>
      <Property.Action className={styles.textButton} onClick={changeView.bind(this, 'backdrop')}>
        Change Overlay
      </Property.Action>
      <Property.Action className={styles.iconButton} onClick={changeView.bind(this, 'style')}>
        <i className={'cleverstory-customize'} />
      </Property.Action>

      {!editMode && view === 'background' &&
        <Background.Dialog id={id}
          value={background}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, id, 'desktop', {
              background: value
            }));
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      }

      {!editMode && view === 'backdrop' &&
        <Background.Dialog id={id}
          value={backdrop}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, id, 'desktop', {
              backdrop: value
            }));
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      }

      {!editMode && view === 'style' && 
        <ModalStyleEditor 
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }} />
      }
    </>
  )
}

ModalWidget.moveableConfig = (editorSession, widget, breakpoint) => {
  var renderDirections = breakpoint === 'desktop' ? ['e', 'w', 's', 'n'] : ['s', 'n'];

  return {
    draggable: false,
    resizable: true,
    rotatable: false,
    selectable: true,
    cloneable: false,
    deletable: false,
    renderDirections: renderDirections,
    onReparent: (widget, parent) => {
      return false
    },
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      frame.set('transform', 'translate', '0px, 0px, 0px');
      return frame.get();
    }
  }
}

ModalWidget.actions = (editor, breakpoint, props) => {
  return <ModalWidgetDesktopAction editorSession={editor} breakpoint={breakpoint} {...props} />
}

const ModalStyleEditor = ({ id, widget, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const properties = _.get(widget, `properties.desktop`, {});

  const border = _.get(properties, 'border', {});
  const borderColor = _.get(border, 'color', null);
  const borderWidth = _.get(border, 'width', 0);
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, 'desktop', value));
  }

  const handleBorderColorChange = (color) => {
    var request = {
      ...properties,
      border: {
        ...border,
        color: color
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderWidthChange = (value) => {
    var request = {
      ...properties,
      border: {
        ...border,
        width: value
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderRadiusChange = (radius) => {
    var request = {
      ...properties,
      border: {
        ...border,
        radius: radius
      }
    }

    handlePropertyChange(request);
  }

  return (
    <Property.Dialog className={styles.styleDialog}
      width={246}
      target={`#${id}`}
      title={'Customize Modal'}
      onCancel={rest.onCancel}
    > 
      <Property.Accordion title={'Border'} className={styles.borderProperty}>
        <Property.Color title={'Color'} clearable={true} value={borderColor} onChange={handleBorderColorChange} />
          <Property name="Stroke Width" className={styles.buttonStyleProperty}>
            <Property.Slider
              min={0}
              max={10}
              step={1}
              input
              value={borderWidth}
              onChange={handleBorderWidthChange}
            />
          </Property>
          <Property name="Corner Radius" className={styles.buttonStyleProperty}>
            <Property.BorderRadius
              value={borderRadius}
              onChange={handleBorderRadiusChange}
            />
          </Property>
      </Property.Accordion>
    </Property.Dialog>
  )
}

function getModalStyles(properties) {
  var styles = {
    transform: 'none'
  };

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `desktop`);

  const border = _.get(defaultProperties, 'border');
  const borderStyles = getBorderStyles(border);

  styles = {
    ...styles,
    ...borderStyles
  }

  return styles;
}

export function getBorderStyles(border) {
  var styles = {};
  if (!border) {
    return styles;
  }

  const topLeft = _.get(border, 'radius.topLeft', null);
  const topRight = _.get(border, 'radius.topRight', null);
  const bottomLeft = _.get(border, 'radius.bottomLeft', null);
  const bottomRight = _.get(border, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const borderStyle = _.get(border, 'style') || 'bottom';
  if (borderStyle !== 'none') {
    const borderWidthString = _.get(border, 'width') || 0;
    var borderWidget = parseInt(borderWidthString) + 'px';

    styles['borderWidth'] = borderWidget;

    const colorRgb = _.get(border, 'color');
    var color = 'transparent';
    if (colorRgb) {
      color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
    }

    styles['borderColor'] = color;
  }

  return styles;
}

function getBackgroundStyle(properties) {
  var styles = {
    overflow: 'hidden'
  };

  const defaultProperties = _.get(properties, `desktop`);

  const border = _.get(defaultProperties, 'border');

  const borderWidthString = _.get(border, 'width') || 0;
  var borderWidget = parseInt(borderWidthString) + 'px';

  var topLeft = _.get(border, 'radius.topLeft', null);
  var topRight = _.get(border, 'radius.topRight', null);
  var bottomLeft = _.get(border, 'radius.bottomLeft', null);
  var bottomRight = _.get(border, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = `calc(${parseInt(topLeft) + 'px'} - ${borderWidget})`;
  }

  if (topRight) {
    styles['borderTopRightRadius'] = `calc(${parseInt(topRight) + 'px'} - ${borderWidget})`;
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = `calc(${parseInt(bottomLeft) + 'px'} - ${borderWidget})`;
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = `calc(${parseInt(bottomRight) + 'px'} - ${borderWidget})`;
  }

  return styles;
}

ModalWidget.processRequest = (widget) => {

  var background = _.get(widget, 'properties.desktop.background');
  var backdrop = _.get(widget, 'properties.desktop.backdrop');

  var backgroundMedia = _.get(background, 'media');
  if (backgroundMedia) {
    background = {
      ...background,
      media: backgroundMedia.id
    }
  }

  var backdropMedia = _.get(backdrop, 'media');
  if (backdropMedia) {
    backdrop = {
      ...backdrop,
      media: backdropMedia.id
    }
  }

  return {
    ...widget,
    properties: {
      ...widget.properties,
      desktop: {
        ...widget.properties.desktop,
        background: background,
        backdrop: backdrop
      }
    }
  }
}

export { ModalWidget };